// Fatia S1: página para agências e gestores de tráfego. Visual da home (home.css).
import { Link } from 'react-router-dom'
import { ArrowRight, Building2, Eye, Link2, Palette, ShieldCheck, Users, type LucideIcon } from 'lucide-react'
import { usePlans } from '../../features/billing/api'
import { Bullets, FeatureCard, Faq, FinalCta, LeadForm, PlanCard, Section } from '../../features/public/Site'
import { Reveal, Words } from '../../features/public/motion'

const CLIENTS: Array<[string, string, 'ok' | 'warn' | 'bad', string]> = [
  ['Doce Ateliê', 'Indo bem', 'ok', 'R$ 1.240 de R$ 1.500'],
  ['Pet Feliz', 'Precisa de atenção', 'warn', 'R$ 2.880 de R$ 3.000'],
  ['Clínica Sorriso', 'Com problema', 'bad', 'Pixel sem dados há 3 dias'],
]

const BENEFITS: Array<[LucideIcon, string, string]> = [
  [Building2, 'Multiempresa de verdade', 'Cada cliente tem as próprias conexões e dados, isolados. Troque de cliente no topo da tela.'],
  [Eye, 'Portal do cliente', 'Convide o cliente com o papel “cliente”: ele vê só a empresa dele e, se você deixar, aprova sugestões.'],
  [Palette, 'Sua marca', 'Logo, cores e nome da agência nos relatórios e no portal do cliente.'],
  [Link2, 'Link de conexão', 'Mande um link e o cliente conecta as contas de anúncio dele, sem precisar de login. Vale por 7 dias.'],
  [Users, 'Equipe com papéis', 'Gestor, analista e leitor por cliente. Analista cria rascunhos, mas não publica nem mexe em orçamento.'],
  [ShieldCheck, 'Segurança', 'Verificação em duas etapas para a equipe, histórico de tudo o que mudou e trilha de auditoria.'],
]

export default function Agencies() {
  const plans = usePlans()
  return (
    <div className="sh">
      <Section hero>
        <div className="sh-hero-grid">
          <div>
            <Reveal as="p" className="sh-label"><Building2 size={14} aria-hidden /> Seu Impulso para agências</Reveal>
            <Words as="h1" immediate delay={120} className="sh-display sh-h1" text="Todos os seus clientes num painel só, com relatórios que *eles entendem.*" />
            <Reveal as="p" delay={500} className="sh-lead">Acompanhe a saúde de cada conta, aplique sugestões em lote, deixe o piloto cuidar do dia a dia e entregue relatórios com a sua marca, sem montar planilha.</Reveal>
            <Reveal delay={650} className="sh-ctas">
              <Link to="/cadastro?tipo=agencia" className="sh-btn sh-btn-cta">Testar grátis <ArrowRight size={16} /></Link>
              <a href="#contato" className="sh-btn sh-btn-ghost">Falar com a gente</a>
            </Reveal>
          </div>
          <Reveal delay={300} className="card">
            <h2 className="sh-card-title">Visão dos clientes</h2>
            <div className="sh-clients">
              {CLIENTS.map(([n, h, c, d]) => (
                <div key={n} className="sh-client">
                  <div><b>{n}</b><span className="d">{d}</span></div>
                  <span className={`sh-pill ${c}`}>{h}</span>
                </div>
              ))}
            </div>
            <p className="sh-fine">Exemplo ilustrativo.</p>
          </Reveal>
        </div>
      </Section>
      <Section alt>
        <Words className="sh-display sh-h2" text="Feito para quem cuida de *vários negócios.*" />
        <div className="sh-grid3">
          {BENEFITS.map(([Icon, t, d]) => <FeatureCard key={t} icon={Icon} title={t}>{d}</FeatureCard>)}
        </div>
      </Section>
      <Section>
        <Words className="sh-display sh-h2" text="Planos para *agências.*" />
        <p className="sh-p sh-p-wide">Cobrança por empresa ativa. Arquivou um cliente? Ele deixa de contar.</p>
        <div className="sh-plans sh-grid3">
          {(plans.data ?? []).filter((p) => p.kind === 'agency').map((p) => <PlanCard key={p.id} plan={p} highlight={p.id === 'agencia_pro'} />)}
        </div>
      </Section>
      <Section alt>
        <div className="sh-split">
          <div>
            <Words className="sh-display sh-h2" text="Perguntas de *agências.*" />
            <div className="sh-note"><Bullets items={['Os dados de cada cliente ficam separados', 'O cliente paga os anúncios direto às plataformas', 'Contrato de tratamento de dados (LGPD) disponível']} /></div>
          </div>
          <Faq items={[
            ['Posso usar a minha conta de administrador do Google Ads (MCC)?', 'Sim. Você pode reaproveitar uma autorização sua, mas cada conexão fica vinculada à empresa certa.'],
            ['O cliente precisa pagar o Seu Impulso?', 'Não. A assinatura é da agência. O cliente só acessa o portal, se você convidar.'],
            ['Como fica a LGPD?', 'O Seu Impulso atua como operador dos dados de anúncio dos seus clientes. Oferecemos contrato de tratamento de dados (DPA) para agências.'],
            ['E quando um cliente sai?', 'Arquive a empresa: a sincronização e o piloto param e o histórico fica guardado por 90 dias. Dá para exportar os dados.'],
          ]} />
        </div>
      </Section>
      <Section id="contato">
        <div className="sh-split">
          <div>
            <Words className="sh-display sh-h2" text="Quer uma *demonstração?*" />
            <p className="sh-p">Conte quantos clientes você atende e quanto eles investem. Respondemos em até 1 dia útil.</p>
          </div>
          <div className="card"><LeadForm kind="agency" /></div>
        </div>
      </Section>
      <FinalCta agency title="Traga seus clientes para o Seu Impulso" />
    </div>
  )
}
